import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import {
  BrandFooter,
  BrandHeader,
  Wordmark,
} from "@/components/runboom/marks";
import { LeaderboardRow } from "@/components/runboom/leaderboard-row";
import {
  CITIES,
  DISTANCES,
  getLeaderboard,
  weeklyMovers,
  type City,
  type Distance,
} from "@/lib/mock";
import { formatRaceTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const searchSchema = z.object({
  distance: z.enum(["5K", "10K", "Half", "Marathon"]).catch("10K"),
  city: z
    .enum(["all", "Cape Town", "Johannesburg", "Stellenbosch"])
    .catch("Cape Town"),
});

export const Route = createFileRoute("/leaderboards")({
  validateSearch: searchSchema,
  component: LeaderboardsPage,
});

function LeaderboardsPage() {
  const { distance, city } = Route.useSearch();
  const navigate = Route.useNavigate();

  const entries = getLeaderboard({ distance, city });

  const setDistance = (d: Distance) =>
    navigate({ search: { distance: d, city } });
  const setCity = (c: City | "all") =>
    navigate({ search: { distance, city: c } });

  return (
    <main className="mx-auto max-w-[1280px] px-5 py-6 sm:px-8 sm:py-10">
      <BrandHeader rightTab="Leaderboards" rightTabTone="lavender" />

      <div className="mt-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Wordmark size="sm" />
        </Link>
        <nav className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.1em] text-fg-muted sm:gap-5">
          <Link to="/oracle" className="hidden hover:text-fg sm:inline">Oracle</Link>
          <Link to="/races" className="hidden hover:text-fg sm:inline">Races</Link>
          <Link to="/wall" className="hidden hover:text-fg sm:inline">Wall</Link>
          <Link
            to="/me"
            className="rounded-full px-3 py-1.5 text-night transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: "var(--color-acid-lime)" }}
          >
            Connect Strava
          </Link>
        </nav>
      </div>

      <header className="mt-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-blood-orange)" }}>
          {city === "all" ? "Every city" : city} · {distance} · all-time
        </div>
        <h1 className="mt-3 font-display-italic font-display text-[56px] leading-[48px] tracking-[-0.015em] text-fg sm:text-[88px] sm:leading-[76px] lg:text-[120px] lg:leading-[100px]">
          LEADERBOARDS.
        </h1>
        <p className="mt-6 max-w-[60ch] font-mono text-[12px] uppercase tracking-[0.12em] text-fg-muted">
          Every distance. Every city.
          <span className="ml-1" style={{ color: "var(--color-lavender)" }}>
            Age-graded for fairness — find out who actually owns your town.
          </span>
        </p>
      </header>

      <section className="mt-8 flex flex-wrap items-center gap-6 border-y border-border py-4">
        <Group label="Distance">
          {DISTANCES.map((d) => (
            <Chip key={d} active={distance === d} onClick={() => setDistance(d)}>
              {d}
            </Chip>
          ))}
        </Group>
        <Group label="City">
          {CITIES.map((c) => (
            <Chip key={c} active={city === c} onClick={() => setCity(c)}>
              {c === "all" ? "All cities" : c}
            </Chip>
          ))}
        </Group>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="rounded-[18px] border border-border bg-bg-elevated">
          <div className="flex items-baseline justify-between border-b border-border px-4 py-3 font-mono text-[11px] uppercase tracking-[0.12em] text-fg-subtle">
            <span>
              Top {entries.length} · {distance} · {city === "all" ? "All cities" : city}
            </span>
            <span>Age grade</span>
          </div>
          {entries.length === 0 ? (
            <div className="px-6 py-12 text-center font-mono text-[12px] uppercase tracking-[0.1em] text-fg-muted">
              No entries match · widen the filters
            </div>
          ) : (
            entries.map((entry) => (
              <LeaderboardRow key={entry.runner.handle} entry={entry} />
            ))
          )}
        </div>

        <aside className="flex flex-col gap-6">
          <WeeklyMovers />
          <AgeGradeExplainer />
        </aside>
      </section>

      <BrandFooter />
    </main>
  );
}

function Group({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="font-sans text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 font-sans text-[13px] transition-colors",
        active
          ? "border-accent bg-accent-soft text-accent"
          : "border-border bg-bg-elevated text-fg-muted hover:border-border-strong hover:text-fg",
      )}
    >
      {children}
    </button>
  );
}

function WeeklyMovers() {
  return (
    <div className="rounded-[18px] border border-border bg-bg-elevated">
      <div className="border-b border-border px-5 py-3 font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-acid-lime)" }}>
        Biggest BOOMs · last 7 days
      </div>
      <ul className="divide-y divide-border">
        {weeklyMovers.map((m) => {
          const delta = m.previousTimeSeconds - m.newTimeSeconds;
          return (
            <li key={`${m.runner.handle}-${m.distanceLabel}`} className="px-5 py-4">
              <Link
                to="/u/$handle"
                params={{ handle: m.runner.handle }}
                className="flex items-center gap-3"
              >
                <img
                  src={m.runner.avatar}
                  alt=""
                  className="h-9 w-9 rounded-full object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-display text-[16px] leading-[16px] tracking-[-0.01em] text-fg">
                    {m.runner.displayName.toUpperCase()}
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
                    {m.distanceLabel} · {m.raceName}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display tabular text-[20px] leading-none tracking-[-0.01em] text-fg">
                    {formatRaceTime(m.newTimeSeconds)}
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.08em]" style={{ color: "var(--color-acid-lime)" }}>
                    −{formatRaceTime(delta)}
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AgeGradeExplainer() {
  return (
    <div className="rounded-[18px] border border-dashed border-border bg-bg-sunken p-5">
      <div className="font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-lavender)" }}>
        About age grade
      </div>
      <p className="mt-2 font-mono text-[12px] leading-[18px] tracking-[0.04em] text-fg-muted">
        Your time divided by the world record for your age and gender. 100% =
        world-record class. 80%+ is national-class. Use it to compare a
        28-year-old's 32:18 to a 52-year-old's 36:04 honestly.
      </p>
    </div>
  );
}
